/**
 * Mini phone mockup that renders an Amber Flex dashboard preview.
 * Pure JSX/CSS — no external images needed.
 * Based on the reference HTML design for the waitlist card.
 */
export function MiniPhoneMockup() {
	return (
		<div className="relative mx-auto h-[340px] w-[170px] rounded-[20px] border-[3px] border-gray-800 bg-white shadow-lg">
			{/* Notch / Dynamic Island */}
			<div className="absolute left-1/2 top-1.5 z-10 h-[10px] w-[50px] -translate-x-1/2 rounded-full bg-gray-800" />

			{/* Screen content */}
			<div className="flex h-full flex-col overflow-hidden rounded-[17px]">
				{/* Status bar */}
				<div className="flex items-center justify-between px-3 pb-0.5 pt-3.5 text-[6px] font-medium text-gray-800">
					<span>9:41</span>
					<span className="flex items-center gap-0.5">
						<StatusBarSignal />
						<StatusBarWifi />
						<StatusBarBattery />
					</span>
				</div>

				{/* Header */}
				<div className="px-3 pb-1.5 pt-0.5 text-center">
					<div className="flex items-center justify-between">
						<svg
							className="h-2.5 w-2.5 text-gray-600"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fillRule="evenodd"
								d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z"
								clipRule="evenodd"
							/>
						</svg>
						<span className="text-[8px] font-bold text-gray-900">
							Dashboard
						</span>
						<svg
							className="h-2.5 w-2.5 text-gray-600"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zm6 0a2 2 0 11-4 0 2 2 0 014 0zm4 2a2 2 0 100-4 2 2 0 000 4z" />
						</svg>
					</div>
				</div>

				{/* Scrollable body */}
				<div className="flex-1 overflow-hidden bg-gray-50 px-2 py-1.5">
					{/* Filter chips */}
					<div className="mb-1.5 flex gap-1">
						<span className="rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[5px] text-gray-600">
							▾ Filters
						</span>
						<span className="rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[5px] text-gray-600">
							This month ▾
						</span>
						<span className="rounded-full border border-gray-200 bg-white px-1.5 py-0.5 text-[5px] text-gray-600">
							Trend: month
						</span>
					</div>

					{/* Overview label */}
					<div className="mb-1 text-[6px] font-bold text-gray-800">
						Overview
					</div>

					{/* KPI grid */}
					<div className="mb-1.5 grid grid-cols-2 gap-1">
						<KpiCell label="Total leads" value="11,932" delta="▲ 12%" up />
						<KpiCell label="Unique" value="5,246" delta="▲ 6%" up />
						<KpiCell label="Processing" value="450" delta="—" />
						<KpiCell label="Bookings" value="634" delta="▲ 40" up />
					</div>
				</div>

				{/* Bottom tab bar */}
				<div className="flex items-center justify-around border-t border-gray-200 bg-white py-1">
					{["Dashboard", "Leads", "Comissions", "Listings", "More"].map(
						(label) => (
							<div key={label} className="flex flex-col items-center gap-0.5">
								<div
									className={`h-1 w-1 rounded-full ${
										label === "Dashboard" ? "bg-blue-600" : "bg-gray-300"
									}`}
								/>
								<span
									className={`text-[4px] ${
										label === "Dashboard"
											? "font-semibold text-blue-600"
											: "text-gray-400"
									}`}
								>
									{label}
								</span>
							</div>
						),
					)}
				</div>
			</div>
		</div>
	);
}

function KpiCell({
	label,
	value,
	delta,
	up,
}: {
	label: string;
	value: string;
	delta: string;
	up?: boolean;
}) {
	return (
		<div className="rounded-md bg-white p-1.5 shadow-[0_0.5px_2px_rgba(0,0,0,0.06)]">
			<div className="text-[5px] text-gray-500">{label}</div>
			<div className="text-[9px] font-bold text-gray-900">{value}</div>
			<div className={`text-[5px] ${up ? "text-green-600" : "text-gray-400"}`}>
				{delta}
			</div>
		</div>
	);
}

/* Tiny status-bar icons */
function StatusBarSignal() {
	return (
		<svg width="8" height="6" viewBox="0 0 16 12" className="text-gray-800">
			<rect x="0" y="8" width="3" height="4" rx="0.5" fill="currentColor" />
			<rect x="4" y="5" width="3" height="7" rx="0.5" fill="currentColor" />
			<rect x="8" y="2" width="3" height="10" rx="0.5" fill="currentColor" />
			<rect x="12" y="0" width="3" height="12" rx="0.5" fill="currentColor" />
		</svg>
	);
}

function StatusBarWifi() {
	return (
		<svg
			width="8"
			height="6"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2.5"
			className="text-gray-800"
		>
			<path d="M1.5 8.5C5.5 4 18.5 4 22.5 8.5" />
			<path d="M5.5 12.5C8 10 16 10 18.5 12.5" />
			<path d="M9 16.5c1.5-1.5 5.5-1.5 7 0" />
			<circle cx="12" cy="19" r="1" fill="currentColor" />
		</svg>
	);
}

function StatusBarBattery() {
	return (
		<svg width="12" height="6" viewBox="0 0 28 13" className="text-gray-800">
			<rect
				x="0.5"
				y="0.5"
				width="23"
				height="12"
				rx="2"
				stroke="currentColor"
				fill="none"
				strokeWidth="1.2"
			/>
			<rect x="2" y="2" width="19" height="8.5" rx="1" fill="currentColor" />
			<rect x="24.5" y="4" width="2.5" height="5" rx="1" fill="currentColor" />
		</svg>
	);
}
